import { useCallback, useEffect, useMemo, useState } from 'react'
import { History, ShieldCheck } from 'lucide-react'
import { Shell, useShellMeta } from '../../admin/Shell.js'
import { SettingsCrumb } from '../../admin/SettingsCrumb.js'
import { Select, Tabs, apiRead, usePermissions } from '../../admin/primitives.js'
import { TextInput } from '../../admin/form.js'
import { classerEchec, lireSupervision, type Lecture } from '../api.js'
import { BandeauLecture, nomSalle } from '../vues/commun.js'
import { AutresEcrans } from './SupervisionGroupePage.js'
import { estSchema, fmtDateParis, parisVersIso, type SchemaReglages } from './modele.js'
import './reglages.css'

/**
 * ADMINISTRATION › SUPERVISION › JOURNAL (écran 14, 21/10/2026).
 *
 * `GET audit` de Sonde, filtrable par auteur, action, groupe et date, page
 * par page (`next_cursor`) ; et, à côté, l'audit de Gestion pour Sonde
 * (`SONDE_*` : réglages relayés, clés, bascule). Le journal est un détail
 * technique : il demande le droit `sonde.details.read`.
 */

interface EntreeAudit {
  id: string
  at: string
  actor_id: string | null
  actor_name: string | null
  actor_kind: string | null
  action: string
  target_kind: string | null
  target_ref: string | null
  detail: Record<string, unknown>
  stepup_at: string | null
}
interface PageAudit { entries: EntreeAudit[]; next_cursor: string | null }
const estPage = (x: unknown): x is PageAudit => typeof x === 'object' && x !== null && Array.isArray((x as PageAudit).entries)
  && (x as PageAudit).entries.every((e) => typeof e?.id === 'string' && typeof e?.at === 'string' && typeof e?.action === 'string')

interface EvenementGestion {
  id: number; occurred_at: string; actor_name: string | null; event_type: string; severity: string
  subject_id: string | null; human_message: string | null
}

const ACTIONS: Record<string, string> = {
  'settings.update': 'Réglages modifiés',
  'credential.set': 'Clé d’accès enregistrée',
  'credential.test': 'Clé d’accès essayée',
  'tls.trust': 'Nouveau certificat accepté',
}
const GENRES_ACTEUR: Record<string, string> = { gestion: 'par Gestion', system: 'système', human: '', worker: 'travail automatique' }

const jourVersIso = (jour: string, fin: boolean): string | null => (jour ? parisVersIso(`${jour}T${fin ? '23:59' : '00:00'}`) : null)

export function SupervisionJournalPage() {
  const meta = useShellMeta()
  const { permissions, loading } = usePermissions()
  const peutVoir = permissions.includes('sonde.details.read')
  const peutGestion = permissions.includes('audit.read')
  const [onglet, setOnglet] = useState<'sonde' | 'gestion'>('sonde')

  const [schema, setSchema] = useState<SchemaReglages | null>(null)
  useEffect(() => { void lireSupervision('/v/settings/schema', estSchema).then((l) => { if (l.etat === 'ok') setSchema(l.data) }) }, [])
  const libelles = useMemo(() => new Map((schema?.settings ?? []).map((s) => [s.key, s.label])), [schema])
  const groupes = useMemo(() => new Map((schema?.groups ?? []).map((g) => [g.code, g.label])), [schema])

  return (
    <Shell active="settings" meta={meta}>
      <main className="lc-page lc-page--management lc-stagger sv-page sv-reg-page">
        <SettingsCrumb fallback="/settings/supervision" />
        <header className="sv-reg-tete">
          <h1 className="lc-title cle-titre"><History size={22} aria-hidden="true" /> Journal de la supervision</h1>
          <p className="lc-subtitle">Qui a changé quoi dans Sonde, et quand : réglages, clés d’accès, certificats, gestes.</p>
        </header>
        {peutGestion ? (
          <div className="sv-section">
            <Tabs tabs={[{ key: 'sonde', label: 'Dans Sonde' }, { key: 'gestion', label: 'Dans Gestion' }]} active={onglet}
                  onChange={(k) => setOnglet(k === 'gestion' ? 'gestion' : 'sonde')} />
          </div>
        ) : null}
        {loading ? null : onglet === 'sonde' ? (
          peutVoir ? <JournalSonde libelles={libelles} groupes={groupes} />
            : <p className="sv-bandeau sv-bandeau--gris sv-section" role="status">Le journal est un détail technique : votre compte ne permet pas de le voir.</p>
        ) : <JournalGestion />}
        {schema ? <AutresEcrans groupes={schema.groups} actuel="journal" /> : null}
      </main>
    </Shell>
  )
}

function JournalSonde({ libelles, groupes }: { libelles: Map<string, string>; groupes: Map<string, string> }) {
  const [auteur, setAuteur] = useState('')
  const [action, setAction] = useState('')
  const [groupe, setGroupe] = useState('')
  const [du, setDu] = useState('')
  const [au, setAu] = useState('')
  const [filtres, setFiltres] = useState('')
  const [lecture, setLecture] = useState<Lecture<PageAudit> | null>(null)
  const [entrees, setEntrees] = useState<EntreeAudit[]>([])
  const [suite, setSuite] = useState<string | null>(null)
  const [enCours, setEnCours] = useState(false)

  const construire = useCallback(() => {
    const p = new URLSearchParams()
    if (auteur.trim()) p.set('actor', auteur.trim().slice(0, 200))
    if (action.trim()) p.set('action', action.trim().slice(0, 100))
    if (groupe) p.set('group', groupe)
    const f = jourVersIso(du, false), t = jourVersIso(au, true)
    if (f) p.set('from', f)
    if (t) p.set('to', t)
    p.set('limit', '50')
    return p.toString()
  }, [auteur, action, groupe, du, au])

  const lire = useCallback(async (q: string, curseur: string | null) => {
    setEnCours(true)
    const l = await lireSupervision(`/v/audit?${q}${curseur ? `&cursor=${encodeURIComponent(curseur)}` : ''}`, estPage)
    setLecture(l)
    if (l.etat === 'ok') {
      setEntrees((e) => curseur ? [...e, ...l.data.entries] : l.data.entries)
      setSuite(l.data.next_cursor)
    }
    setEnCours(false)
  }, [])
  useEffect(() => { void lire(filtres, null) }, [lire, filtres])

  const appliquer = () => { const q = construire(); if (q === filtres) void lire(q, null); else setFiltres(q) }
  const optionsGroupes = [{ value: '', label: 'Tous les groupes' }, ...[...groupes].map(([value, label]) => ({ value, label }))]

  return (
    <>
      <section className="lc-panel sv-section" aria-label="Filtres du journal">
        <form className="lc-panel__body sv-jour-filtres" onSubmit={(e) => { e.preventDefault(); appliquer() }}>
          <label className="sv-jour-filtre"><span className="lc-label">Auteur</span>
            <TextInput value={auteur} onChange={setAuteur} maxLength={200} ariaLabel="Auteur" placeholder="nom ou identifiant" /></label>
          <label className="sv-jour-filtre"><span className="lc-label">Action</span>
            <TextInput value={action} onChange={setAction} maxLength={100} ariaLabel="Action" placeholder="settings.update…" /></label>
          <div className="sv-jour-filtre"><span className="lc-label">Groupe</span>
            <Select value={groupe} options={optionsGroupes} onChange={setGroupe} ariaLabel="Groupe" /></div>
          <label className="sv-jour-filtre"><span className="lc-label">Du</span>
            <TextInput type="date" value={du} onChange={setDu} ariaLabel="Du" /></label>
          <label className="sv-jour-filtre"><span className="lc-label">Au</span>
            <TextInput type="date" value={au} onChange={setAu} ariaLabel="Au" /></label>
          <div className="sv-jour-filtre sv-jour-filtre--bouton"><button type="submit" className="lc-btn lc-btn--primary" disabled={enCours}>Filtrer</button></div>
        </form>
      </section>
      {lecture && lecture.etat !== 'ok' ? <BandeauLecture lecture={lecture} /> : null}
      <section className="lc-panel sv-section" aria-label="Entrées du journal de Sonde">
        <div className="lc-panel__body">
          {lecture?.etat === 'ok' && entrees.length === 0 ? <p className="lc-hint">Aucune entrée pour ces filtres.</p> : null}
          {!lecture ? <p className="lc-hint">Lecture du journal…</p> : null}
          <ol className="sv-jour-liste">
            {entrees.map((e) => <LigneJournal key={e.id} e={e} libelles={libelles} groupes={groupes} />)}
          </ol>
          {suite ? (
            <p><button type="button" className="lc-btn" disabled={enCours} onClick={() => { void lire(filtres, suite) }}>Entrées plus anciennes</button></p>
          ) : null}
          <p className="lc-hint">Source : journal de Sonde, heure de Paris.</p>
        </div>
      </section>
    </>
  )
}

function LigneJournal({ e, libelles, groupes }: { e: EntreeAudit; libelles: Map<string, string>; groupes: Map<string, string> }) {
  const cles = Array.isArray(e.detail['keys']) ? (e.detail['keys'] as unknown[]).filter((k): k is string => typeof k === 'string') : []
  const portee = typeof e.detail['scope_kind'] === 'string' && e.detail['scope_kind'] !== 'global'
    ? (e.detail['scope_kind'] === 'zone' ? nomSalle(String(e.detail['scope_ref'] ?? '')) : `${String(e.detail['scope_kind'])} ${String(e.detail['scope_ref'] ?? '')}`) : null
  const motif = typeof e.detail['reason'] === 'string' && e.detail['reason'] ? String(e.detail['reason']) : null
  const cible = e.target_kind === 'setting_group' && e.target_ref
    ? (groupes.get(e.target_ref) ?? (e.target_ref.startsWith('source:') ? `Source ${e.target_ref.slice(7)}` : e.target_ref))
    : [e.target_kind, e.target_ref].filter(Boolean).join(' ')
  const genre = e.actor_kind ? GENRES_ACTEUR[e.actor_kind] ?? e.actor_kind : ''
  return (
    <li className="sv-jour-ligne">
      <span className="sv-jour-ligne__quand sv-mono">{fmtDateParis(e.at)}</span>
      <div className="sv-jour-ligne__corps">
        <p className="sv-jour-ligne__titre">
          <span>{ACTIONS[e.action] ?? <span className="sv-mono">{e.action}</span>}</span>
          {cible ? <span className="sv-jour-ligne__cible">· {cible}</span> : null}
          {e.stepup_at ? <span className="sv-reg-badge sv-reg-badge--stepup"><ShieldCheck size={13} aria-hidden="true" /> identité confirmée</span> : null}
        </p>
        <p className="sv-jour-ligne__meta">
          {e.actor_name ?? 'auteur inconnu'}{genre ? ` (${genre})` : ''}{portee ? ` · portée ${portee}` : ''}
        </p>
        {cles.length > 0 ? <p className="sv-jour-ligne__cles">{cles.map((k) => libelles.get(k) ?? k).join(' · ')}</p> : null}
        {motif ? <p className="sv-jour-ligne__motif">Motif : {motif}</p> : null}
      </div>
    </li>
  )
}

function JournalGestion() {
  const [types, setTypes] = useState<string[]>([])
  const [action, setAction] = useState('SONDE_SETTINGS_UPDATED')
  const [etat, setEtat] = useState<{ items: EvenementGestion[] } | { erreur: string } | null>(null)
  useEffect(() => {
    let vivant = true
    setEtat(null)
    void apiRead<{ items?: EvenementGestion[]; event_types?: string[]; error?: string; message?: string }>(`/api/v1/audit?action=${encodeURIComponent(action)}&limit=50`)
      .then((r) => {
        if (!vivant) return
        if (r.status >= 300 || !Array.isArray(r.data?.items)) { setEtat({ erreur: classerEchec(r.status, r.data).message }); return }
        setEtat({ items: r.data.items })
        setTypes((r.data.event_types ?? []).filter((t) => t.startsWith('SONDE_')))
      })
      .catch(() => { if (vivant) setEtat({ erreur: 'Gestion n’a pas répondu.' }) })
    return () => { vivant = false }
  }, [action])
  const options = [...new Set(['SONDE_SETTINGS_UPDATED', ...types])].map((t) => ({ value: t, label: t }))
  return (
    <section className="lc-panel sv-section" aria-label="Journal de Gestion pour Sonde">
      <div className="lc-panel__body">
        <div className="sv-jour-filtre sv-jour-filtre--seul"><span className="lc-label">Événement de Gestion</span>
          <Select value={action} options={options} onChange={setAction} ariaLabel="Événement de Gestion" /></div>
        {etat === null ? <p className="lc-hint">Lecture du journal de Gestion…</p> : null}
        {etat && 'erreur' in etat ? <p className="sv-bandeau sv-bandeau--gris" role="status">{etat.erreur}</p> : null}
        {etat && 'items' in etat && etat.items.length === 0 ? <p className="lc-hint">Aucun événement.</p> : null}
        {etat && 'items' in etat ? (
          <ol className="sv-jour-liste">
            {etat.items.map((x) => (
              <li key={x.id} className="sv-jour-ligne">
                <span className="sv-jour-ligne__quand sv-mono">{fmtDateParis(x.occurred_at)}</span>
                <div className="sv-jour-ligne__corps">
                  <p className="sv-jour-ligne__titre"><span>{x.human_message ?? x.event_type}</span></p>
                  <p className="sv-jour-ligne__meta">{x.actor_name ?? 'auteur inconnu'}{x.subject_id ? ` · ${x.subject_id}` : ''}</p>
                </div>
              </li>
            ))}
          </ol>
        ) : null}
        <p className="lc-hint">Source : audit de Gestion.</p>
      </div>
    </section>
  )
}
